/**
 * <nb-carousel> — multi-item scroll-snap carousel.
 *
 * Distinct from <nb-slider>, which shows exactly one item per view. Here several
 * cards are visible at once, so paging moves by a single card rather than a
 * full viewport, and the arrows disable at the ends instead of wrapping —
 * wrapping is disorienting when the previous items are still on screen.
 *
 * The track scrolls natively, so swipe and keyboard scrolling work with no JS.
 */
class NbCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.nb-carousel__track');
    if (!this.track) return;

    this.prevBtn = this.querySelector('[data-nb-carousel-prev]');
    this.nextBtn = this.querySelector('[data-nb-carousel-next]');

    this.prevBtn?.addEventListener('click', () => this.page(-1));
    this.nextBtn?.addEventListener('click', () => this.page(1));

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.sync());
    this.resizeObserver.observe(this.track);

    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.raf);
  }

  /** Distance of one card plus the flex gap between cards. */
  get step() {
    const first = this.track.firstElementChild;
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  page(direction) {
    this.track.scrollBy({
      left: this.step * direction,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  onScroll = () => {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.sync());
  };

  sync() {
    if (!this.track) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // A pixel of tolerance, since fractional layout widths mean scrollLeft
    // rarely lands exactly on the maximum.
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    const overflows = scrollWidth > clientWidth + 1;

    this.toggleAttribute('data-static', !overflows);

    if (this.prevBtn) this.prevBtn.disabled = atStart;
    if (this.nextBtn) this.nextBtn.disabled = atEnd;
  }
}

if (!customElements.get('nb-carousel')) {
  customElements.define('nb-carousel', NbCarousel);
}
